export const GA_MEASUREMENT_ID = 'G-S5P40BMFHB';
export const ANALYTICS_CONSENT_KEY = 'trr_analytics_consent_v1';

export type AnalyticsConsent = 'granted' | 'denied' | null;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  return window.gtag;
}

export function getAnalyticsConsent(): AnalyticsConsent {
  try {
    const value = localStorage.getItem(ANALYTICS_CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

function loadGoogleTag() {
  if (document.querySelector(`script[data-trr-ga="${GA_MEASUREMENT_ID}"]`)) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.dataset.trrGa = GA_MEASUREMENT_ID;
  document.head.appendChild(script);

  const gtag = ensureGtag();
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

export function initializeAnalytics() {
  const gtag = ensureGtag();
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
  gtag('set', 'ads_data_redaction', true);

  if (getAnalyticsConsent() === 'granted') {
    gtag('consent', 'update', { analytics_storage: 'granted' });
    loadGoogleTag();
  }
}

export function setAnalyticsConsent(consent: Exclude<AnalyticsConsent, null>) {
  try {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
  } catch {
    // Analytics remains disabled when storage is unavailable.
  }

  const gtag = ensureGtag();
  gtag('consent', 'update', {
    analytics_storage: consent,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  if (consent === 'granted') loadGoogleTag();
}

function normalizedPath(pathname: string): string {
  if (/^\/listing\/[^/]+/.test(pathname)) return '/listing/:id';
  if (/^\/claim\/[^/]+/.test(pathname)) return '/claim/:listingId';
  if (/^\/review\/verify\/[^/]+/.test(pathname)) return '/review/verify/:token';
  if (/^\/resources\/[^/]+/.test(pathname)) return '/resources/:slug';
  return pathname || '/';
}

export function trackPageView(pathname: string, title = document.title) {
  if (getAnalyticsConsent() !== 'granted') return;
  ensureGtag()('event', 'page_view', {
    page_title: title,
    page_location: `${window.location.origin}${normalizedPath(pathname)}`,
    page_path: normalizedPath(pathname),
    surface: 'spa',
  });
}

export function trackAnalyticsEvent(
  name: string,
  parameters: Record<string, string | number | boolean> = {},
) {
  if (getAnalyticsConsent() !== 'granted') return;
  ensureGtag()('event', name, { surface: 'spa', ...parameters });
}

